import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Logo, LogoArtwork } from "@/components/Logo";
import { LanguageToggle } from "@/components/LanguageToggle";
import { useI18n } from "@/contexts/I18nContext";
import { useAdminAuth } from "@/contexts/AdminAuthContext";

export default function AdminLogin() {
  const { t } = useI18n();
  const { signIn } = useAdminAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await signIn(email, password);
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error(t.admin.invalidCredentials);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen noise bg-background">
      <div
        className="absolute inset-x-0 top-0 h-72 -z-10 opacity-90"
        style={{
          background:
            "radial-gradient(900px circle at 80% 0%, rgba(200,30,58,0.10) 0%, transparent 55%), radial-gradient(1200px circle at 20% -10%, hsl(var(--accent)) 0%, transparent 55%)",
        }}
      />
      <div className="mx-auto flex max-w-md flex-col px-4 pt-safe">
        <div className="flex items-center justify-end py-4">
          <LanguageToggle />
        </div>
        <div className="flex flex-col items-center text-center">
          <LogoArtwork size={150} className="drop-shadow-sm" />
        </div>
        <h1 className="mt-6 font-serif-display text-3xl font-semibold text-center">{t.admin.title}</h1>
        <p className="text-sm text-muted-foreground text-center">{t.admin.login}</p>

        <Card className="mt-6 rounded-2xl shadow-sm ring-1 ring-black/5">
          <CardContent className="p-5">
            <form className="space-y-4" onSubmit={onSubmit}>
              <div className="space-y-1.5">
                <Label htmlFor="admin-email">{t.admin.emailLabel}</Label>
                <Input
                  id="admin-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-12"
                  data-testid="admin-login-email"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="admin-password">{t.admin.password}</Label>
                <Input
                  id="admin-password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-12"
                  data-testid="admin-login-password"
                />
              </div>
              <Button type="submit" className="h-12 w-full" disabled={submitting} data-testid="admin-login-submit">
                {submitting ? "..." : t.admin.loginButton}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
